import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatINR, BACKEND_URL } from "@/lib/api";
import { Star, ShoppingBag, Heart, Truck, ShieldCheck, RotateCcw, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { motion, AnimatePresence } from "framer-motion";
import { effectiveUnitPrice, sortedTiers } from "@/lib/pricing";
import useDocumentMeta from "@/lib/useDocumentMeta";

const BACKEND = BACKEND_URL;
const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${BACKEND}${u}`;
};

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeIdx, setActiveIdx] = useState(0);
  const { addItem, toggleWishlist, wishlist } = useCart();
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const [activeTab, setActiveTab] = useState("description");
  const imgWrapRef = useRef(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActiveIdx(0);
    }).catch(() => setProduct(null));
    // Fire view tracking for approved partners (server filters non-partners)
    api.post(`/salon/track-view/${id}`).catch(() => {});
  }, [id]);

  const gallery = useMemo(() => {
    if (!product) return [];
    if (product.images && product.images.length > 0) return product.images;
    return product.image ? [product.image] : [];
  }, [product]);

  if (!product) return <div className="pt-40 text-center overline">Loading…</div>;
  const inWish = wishlist.includes(product.id);
  const mainImg = gallery[activeIdx] || product.image;

  const next = () => setActiveIdx((i) => (i + 1) % gallery.length);
  const prev = () => setActiveIdx((i) => (i - 1 + gallery.length) % gallery.length);

  // Zoom: track cursor position over the image for desktop hover zoom
  const onMouseMove = (e) => {
    const el = imgWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoom((z) => ({ ...z, x, y }));
  };

  // SEO/meta — use admin overrides when present, otherwise sensible defaults
  const seoTitle = product.seo_title || `${product.name} — Blisson Professional`;
  const seoDesc =
    product.seo_description ||
    (product.description ? product.description.replace(/\s+/g, " ").slice(0, 158) : `Buy ${product.name} at Blisson Professional — salon-grade luxury beauty.`);
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/product/${product.id}` : undefined;

  // Build tabs list (only non-empty)
  const tabs = [
    { key: "description", label: "Description", body: product.description },
    { key: "benefits", label: "Benefits", body: product.benefits },
    { key: "how_to_use", label: "How to Use", body: product.how_to_use },
    { key: "ingredients", label: "Ingredients", body: product.ingredients },
    { key: "suitable_for", label: "Suitable For", body: product.suitable_for },
  ].filter((t) => t.body && String(t.body).trim().length > 0);
  const hasFaqs = Array.isArray(product.faqs) && product.faqs.filter((f) => (f.q || "").trim() && (f.a || "").trim()).length > 0;
  if (hasFaqs) tabs.push({ key: "faqs", label: "FAQs", body: null });
  const currentTab = tabs.find((t) => t.key === activeTab) || tabs[0];

  return (
    <div className="pt-32 pb-20" data-testid="product-detail-page">
      <SEO title={seoTitle} description={seoDesc} image={absoluteUrl(mainImg)} canonical={canonical} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="overline mb-8">
          <Link to="/">Home</Link> · <Link to="/shop">Shop</Link> · <span className="text-[#C9A961]">{product.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="flex flex-col-reverse md:flex-row gap-4 min-w-0">
            {gallery.length > 1 && (
              <div className="flex md:flex-col gap-3 md:w-20 max-w-full overflow-x-auto md:overflow-y-auto no-scrollbar -mx-1 px-1" data-testid="product-thumbs">
                {gallery.map((src, i) => (
                  <button
                    key={src + i}
                    onClick={() => setActiveIdx(i)}
                    className={`relative flex-shrink-0 w-20 h-24 md:w-full md:h-24 overflow-hidden border transition-colors ${
                      i === activeIdx ? "border-[#C9A961]" : "border-transparent hover:border-black/20"
                    }`}
                    data-testid={`product-thumb-${i}`}
                    aria-label={`Image ${i + 1}`}
                  >
                    <img src={absoluteUrl(src)} alt={`${product.name} thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div
              ref={imgWrapRef}
              className="flex-1 relative bg-[#F8F5F0] aspect-square overflow-hidden group cursor-zoom-in"
              data-testid="product-detail-main-image"
              onMouseEnter={() => setZoom((z) => ({ ...z, active: true }))}
              onMouseLeave={() => setZoom((z) => ({ ...z, active: false }))}
              onMouseMove={onMouseMove}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={mainImg}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  src={absoluteUrl(mainImg)}
                  alt={`${product.name} — ${product.category}${gallery.length > 1 ? ` (image ${activeIdx + 1} of ${gallery.length})` : ""}`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out"
                  style={
                    zoom.active
                      ? { transform: `scale(2)`, transformOrigin: `${zoom.x}% ${zoom.y}%` }
                      : undefined
                  }
                  data-testid="product-main-image"
                />
              </AnimatePresence>

              {gallery.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                    data-testid="product-image-prev"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                    data-testid="product-image-next"
                    aria-label="Next image"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase bg-black/70 backdrop-blur text-white px-3 py-1">
                    {activeIdx + 1} / {gallery.length}
                  </div>
                </>
              )}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="overline mb-3">{product.category}</div>
            <h1 className="font-serif-display text-4xl md:text-6xl tracking-tighter font-light leading-[1.02]">{product.name}</h1>

            <div className="flex items-center gap-1 mt-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={14} fill={i <= Math.round(product.rating) ? "#C9A961" : "transparent"} strokeWidth={1.2} color="#C9A961" />
              ))}
              <span className="text-xs text-[#5C5C5C] ml-2">{product.review_count} verified reviews</span>
            </div>

            <div className="flex items-baseline gap-3 mt-6">
              <span className="font-serif-display text-4xl" data-testid="pdp-unit-price">{formatINR(effectiveUnitPrice(product.price, product.tiers, qty))}</span>
              {product.original_price > product.price && (
                <span className="text-[#5C5C5C] line-through text-lg">{formatINR(product.original_price)}</span>
              )}
              {effectiveUnitPrice(product.price, product.tiers, qty) < product.price && (
                <span className="text-xs tracking-[0.22em] uppercase bg-[#C9A961]/20 text-[#9d8345] px-2 py-1">
                  Save {formatINR((product.price - effectiveUnitPrice(product.price, product.tiers, qty)) * qty)}
                </span>
              )}
            </div>

            {product.tiers && product.tiers.length > 0 && (
              <div className="mt-6" data-testid="pdp-tiers">
                <div className="overline mb-3 flex items-center gap-2">
                  <Tag size={12} className="text-[#C9A961]" /> Bulk Offers — buy more, save more
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {sortedTiers(product.tiers).map((t, i) => {
                    const selected = qty >= Number(t.min_qty) && (i === sortedTiers(product.tiers).length - 1 || qty < Number(sortedTiers(product.tiers)[i + 1].min_qty));
                    const savePct = product.price > t.price ? Math.round(((product.price - Number(t.price)) / product.price) * 100) : 0;
                    return (
                      <button
                        key={t.min_qty}
                        onClick={() => setQty(Number(t.min_qty))}
                        className={`text-left p-3 border transition-colors relative ${
                          selected ? "border-black bg-black text-white" : "border-black/15 hover:border-black"
                        }`}
                        data-testid={`pdp-tier-${t.min_qty}`}
                      >
                        <div className="overline text-[10px]">{t.label || `${t.min_qty} PCS`}</div>
                        <div className="font-serif-display text-xl mt-0.5">{formatINR(t.price)}<span className={`text-[10px] ml-1 ${selected ? "text-white/70" : "text-[#5C5C5C]"}`}>/ unit</span></div>
                        {savePct > 0 && (
                          <div className={`text-[10px] mt-0.5 ${selected ? "text-[#C9A961]" : "text-[#9d8345]"}`}>
                            Save {savePct}%
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-base text-[#3a3a3a] mt-6 leading-relaxed max-w-lg whitespace-pre-line" data-testid="product-description">{product.description}</p>

            <div className="flex flex-wrap items-center gap-2 mt-6">
              {product.badges?.map((b) => (
                <span key={b} className="text-[10px] tracking-[0.22em] uppercase border border-[#C9A961] text-[#9d8345] px-3 py-1">
                  {b}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-10">
              <div className="flex items-center border border-black/15">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-14 hover:bg-[#F8F5F0]" data-testid="pdp-qty-minus">−</button>
                <span className="w-12 text-center" data-testid="pdp-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="w-11 h-14 hover:bg-[#F8F5F0]" data-testid="pdp-qty-plus">+</button>
              </div>
              <button onClick={() => addItem(product, qty)} className="btn-primary flex-1" data-testid="pdp-add-to-cart">
                <ShoppingBag size={14} /> Add to Bag
              </button>
              <button
                onClick={() => toggleWishlist(product.id)}
                className={`w-14 h-14 border ${inWish ? "border-[#C9A961] text-[#C9A961]" : "border-black/15 text-black"} flex items-center justify-center hover:bg-[#F8F5F0]`}
                data-testid="pdp-wishlist"
                aria-label="Wishlist"
              >
                <Heart size={16} fill={inWish ? "#C9A961" : "transparent"} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-10 pt-8 border-t border-black/5">
              {[
                { icon: Truck, label: "Free shipping ₹499+" },
                { icon: ShieldCheck, label: "Authenticity guarantee" },
                { icon: RotateCcw, label: "30-day returns" },
              ].map((b, i) => (
                <div key={i} className="text-center">
                  <b.icon size={20} strokeWidth={1.2} className="mx-auto text-[#C9A961] mb-2" />
                  <div className="text-xs text-[#5C5C5C]">{b.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Product tabs */}
        {(tabs.length > 0 || hasFaqs) && (
          <div className="mt-16 lg:mt-24 border-t border-black/10 pt-10" data-testid="pdp-tabs-section">
            <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-black/10 overflow-x-auto no-scrollbar" role="tablist">
              {tabs.map((t) => {
                const active = currentTab?.key === t.key;
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(t.key)}
                    className={`pb-3 text-[11px] tracking-[0.28em] uppercase transition-colors whitespace-nowrap relative ${
                      active ? "text-black" : "text-[#5C5C5C] hover:text-black"
                    }`}
                    data-testid={`pdp-tab-${t.key}`}
                  >
                    {t.label}
                    {active && <span className="absolute left-0 right-0 -bottom-px h-px bg-[#C9A961]" />}
                  </button>
                );
              })}
            </div>

            <div className="py-8 max-w-3xl" data-testid={`pdp-tab-panel-${currentTab?.key || "none"}`}>
              {currentTab?.key === "faqs" ? (
                <div className="space-y-4">
                  {product.faqs
                    .filter((f) => (f.q || "").trim() && (f.a || "").trim())
                    .map((f, i) => (
                      <details
                        key={i}
                        className="group border border-black/10 px-5 py-4 open:border-[#C9A961]/40 transition-colors"
                        data-testid={`pdp-faq-${i}`}
                      >
                        <summary className="cursor-pointer flex items-center justify-between gap-3 list-none">
                          <span className="font-serif-display text-lg tracking-tight">{f.q}</span>
                          <ChevronRight
                            size={16}
                            className="text-[#C9A961] transition-transform group-open:rotate-90 flex-shrink-0"
                          />
                        </summary>
                        <p className="mt-3 text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line">{f.a}</p>
                      </details>
                    ))}
                </div>
              ) : (
                <p className="text-base text-[#3a3a3a] leading-relaxed whitespace-pre-line">
                  {currentTab?.body}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile Add-to-Cart bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-black/10 shadow-[0_-6px_24px_rgba(0,0,0,0.08)]" data-testid="sticky-mobile-cart-bar">
        <div className="flex items-center gap-3 px-4 py-3 safe-bottom">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Total</span>
            <span className="font-serif-display text-xl leading-none">{formatINR(product.price * qty)}</span>
          </div>
          <div className="flex items-center border border-black/15 ml-2">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-11 text-lg" data-testid="sticky-qty-minus" aria-label="Decrease">−</button>
            <span className="w-7 text-center text-sm" data-testid="sticky-qty">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="w-9 h-11 text-lg" data-testid="sticky-qty-plus" aria-label="Increase">+</button>
          </div>
          <button
            onClick={() => addItem(product, qty)}
            className="flex-1 bg-black text-white text-[11px] tracking-[0.22em] uppercase h-11 flex items-center justify-center gap-2 hover:bg-[#C9A961] transition-colors"
            data-testid="sticky-add-to-cart"
          >
            <ShoppingBag size={14} /> Add to Bag
          </button>
        </div>
      </div>
    </div>
  );
}

function SEO({ title, description, image, canonical }) {
  useDocumentMeta({ title, description, image, canonical });
  return null;
}
